import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Divider, Typography } from '@/primitives';
import DynamicFilterRow, {
  type DynamicFilterRowData,
} from './DynamicFilterRow';
import type { FilterInput } from '../../constants';
import type { DynamicFieldOption } from '../constants';
import {
  isIdentChipQuickSearchField,
  isIdentChipStyleFilter,
} from '../animalIdentChipFilter';

let nextRowId = 1;
function createEmptyRow(): DynamicFilterRowData {
  return { id: String(nextRowId++), field: '', operator: '', value: '' };
}

interface Props {
  fieldOptions: DynamicFieldOption[];
  onChange: (filters: FilterInput[]) => void;
  initialFilters?: FilterInput[];
  resetKey?: number;
  allowAdd?: boolean;
}

export default function DynamicFilterSection({
  fieldOptions,
  onChange,
  initialFilters,
  resetKey,
  allowAdd = true,
}: Props) {
  const [rows, setRows] = useState<DynamicFilterRowData[]>(() => {
    if (initialFilters && initialFilters.length > 0) {
      const hydrated = initialFilters.map((f) => ({
        id: String(nextRowId++),
        field: f.field,
        operator: f.operator,
        value: f.value,
      }));
      hydrated.push(createEmptyRow());
      return hydrated;
    }
    return [createEmptyRow()];
  });

  const prevResetKey = useRef(resetKey);
  useEffect(() => {
    if (resetKey !== prevResetKey.current) {
      prevResetKey.current = resetKey;
      const newRows = [createEmptyRow()];
      setRows(newRows);
      onChange([]);
    }
  }, [resetKey, onChange]);

  const prevInitialFiltersRef = useRef(initialFilters);
  useEffect(() => {
    if (initialFilters === prevInitialFiltersRef.current) return;
    prevInitialFiltersRef.current = initialFilters;

    if (!initialFilters || initialFilters.length === 0) {
      setRows([createEmptyRow()]);
      return;
    }

    const hydrated: DynamicFilterRowData[] = initialFilters.map((f) => ({
      id: String(nextRowId++),
      field: f.field,
      operator: f.operator,
      value: f.value,
    }));
    hydrated.push(createEmptyRow());
    setRows(hydrated);
  }, [initialFilters]);

  const emitChange = useCallback(
    (nextRows: DynamicFilterRowData[]) => {
      const filters: FilterInput[] = nextRows
        .filter(
          (r) =>
            r.field &&
            (r.operator ||
              (isIdentChipQuickSearchField(r.field) &&
                isIdentChipStyleFilter(r.operator, r.value) &&
                r.value.trim())),
        )
        .map((r) => ({ field: r.field, operator: r.operator, value: r.value }));
      onChange(filters);
    },
    [onChange],
  );

  const handleUpdate = useCallback(
    (id: string, patch: Partial<Omit<DynamicFilterRowData, 'id'>>) => {
      setRows((prev) => {
        const next = prev.map((r) => (r.id === id ? { ...r, ...patch } : r));

        const lastRow = next[next.length - 1];
        if (lastRow && (lastRow.field || lastRow.operator || lastRow.value)) {
          next.push(createEmptyRow());
        }

        emitChange(next);
        return next;
      });
    },
    [emitChange],
  );

  const handleDelete = useCallback(
    (id: string) => {
      setRows((prev) => {
        const next = prev.filter((r) => r.id !== id);
        if (next.length === 0) {
          next.push(createEmptyRow());
        }
        emitChange(next);
        return next;
      });
    },
    [emitChange],
  );

  const filledRowCount = rows.filter(
    (r) => r.field || r.operator || r.value,
  ).length;
  const canAdd = allowAdd && fieldOptions.length > 0;
  const visibleRows = canAdd
    ? rows
    : rows.filter((row) => row.field || row.operator || row.value);
  if (visibleRows.length === 0) return null;

  return (
    <Box sx={{ mt: 2 }}>
      <Divider sx={{ mb: 2 }} />
      <Typography
        variant="labelmedium"
        sx={{ fontWeight: 600, mb: 1.5, display: 'block' }}
      >
        Additional Filters
      </Typography>
      {visibleRows.map((row) => (
        <DynamicFilterRow
          key={row.id}
          row={row}
          fieldOptions={
            fieldOptions.some((option) => option.value === row.field) ||
            !row.field
              ? fieldOptions
              : [
                  ...fieldOptions,
                  {
                    value: row.field,
                    label: row.field.replace(/^__lookup__/, ''),
                  },
                ]
          }
          lockField={!canAdd}
          onUpdate={handleUpdate}
          onDelete={handleDelete}
          showDelete={!canAdd || filledRowCount > 1 || rows.length > 1}
        />
      ))}
    </Box>
  );
}
